import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  NgZone,
  output,
  signal,
  TemplateRef,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { PaginationParams, ProductDetails, ProductLegacy } from '@models';
import {
  DEFAULT_FILTERS_FORM_STATE,
  FILTER_OPTIONS_SERVICE,
  FormState,
  ProductListCardComponent,
  ProductListFiltersComponent,
  SeeMoreItemsButtonComponent,
} from '@shared';
import { SkeletonTemplateService } from '@skeleton-loader';
import { PriceFilters, PRODUCT_LIST_FACADE } from './product-list.model';
import { ProductListStateService, UrlFiltersService, UserService } from '@core';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Subscription, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'lib-product-list',
  imports: [
    CommonModule,
    MatButtonModule,
    ProductListCardComponent,
    ProductListFiltersComponent,
    SeeMoreItemsButtonComponent,
  ],
  templateUrl: './product-list.component.html',
  styleUrl: './product-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductListComponent {
  skeletonService = inject(SkeletonTemplateService);
  filterOptionsService = inject(FILTER_OPTIONS_SERVICE, { optional: true });

  private readonly productListFacade = inject(PRODUCT_LIST_FACADE);
  private readonly urlFiltersService = inject(UrlFiltersService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly productListStateService = inject(ProductListStateService);
  private readonly ngZone = inject(NgZone);

  private userSubscription: Subscription | null = null;
  private scrollPositionToRestore: number | null = null;
  private isRestoringFromHistory = false;
  private scrollRestoreAttempted = false;

  filterComponent = viewChild<ProductListFiltersComponent>(ProductListFiltersComponent);

  emptyListTemplate = input<TemplateRef<unknown>>();
  // Filters from query params
  initialFilters = signal<FormState>(DEFAULT_FILTERS_FORM_STATE);

  onShowProduct = output<ProductDetails | ProductLegacy>();
  // RCP-441: wishlist disabled
  // toggleWishlist = output<ProductDetails | ProductLegacy>();
  addToCart = output<ProductDetails | ProductLegacy>();

  readonly vm = computed(() => ({
    products: this.productListFacade.products(),
    totalCount: this.productListFacade.totalCount() || this.productListFacade.products().length,
    subtitle: this.productListFacade.subtitle?.(),
    title: this.productListFacade.title?.(),
    isLoading: this.productListFacade.isLoading(),
    initialLoading: this.productListFacade.initialLoading(),
    showPaginationCount: false,
  }));

  constructor() {
    this.initFilters();

    // Get current route URL to match saved state
    const currentRouteUrl = this.router.url;

    // Try to restore from service (survives component destruction/recreation)
    const savedState = this.productListStateService.getAndConsumeState(currentRouteUrl);

    if (savedState) {
      if (savedState.products && Array.isArray(savedState.products)) {
        this.isRestoringFromHistory = true;
        this.productListFacade.setInitialState(savedState.products, savedState.totalCount, savedState.filters);

        // Store scroll position to restore after view is initialized
        if (typeof savedState.scrollY === 'number') {
          this.scrollPositionToRestore = savedState.scrollY;
          this.scrollRestoreAttempted = false; // Reset flag for new restoration
        }

        // Reset flag after a short delay to allow navigation to complete
        setTimeout(() => {
          this.isRestoringFromHistory = false;
        }, 100);
      } else {
        this.initFetchingWithDelay();
      }
    } else {
      this.initFetchingWithDelay();
    }

    effect(() => {
      this.bindRouteChangeToFilterRefetch();
    });

    // Watch for when products are loaded and restore scroll immediately
    effect(() => {
      const vm = this.vm();
      if (
        this.scrollPositionToRestore !== null &&
        !this.scrollRestoreAttempted &&
        vm.products.length > 0 &&
        !vm.initialLoading
      ) {
        this.scrollRestoreAttempted = true;
        this.restoreScrollPosition();
      }
    });
  }

  private initFetchingWithDelay(): void {
    const timeout = setTimeout(() => {
      this.initFetching();
    }, 100);
    this.destroyRef.onDestroy(() => clearTimeout(timeout));
  }

  private restoreScrollPosition(): void {
    if (this.scrollPositionToRestore === null) {
      return;
    }

    const targetScroll = this.scrollPositionToRestore;

    // Restore immediately using requestAnimationFrame for smooth execution
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => {
        window.scrollTo({ top: targetScroll, behavior: 'auto' });

        // Single re-apply after a brief moment to catch any router interference
        requestAnimationFrame(() => {
          const currentScroll = window.scrollY;
          if (Math.abs(currentScroll - targetScroll) > 10) {
            window.scrollTo({ top: targetScroll, behavior: 'auto' });
          }
          this.scrollPositionToRestore = null;
        });
      });
    });
  }

  seeMoreProducts(): void {
    this.productListFacade.seeMoreProducts();
  }

  onFiltersChanged({ isAvailablePointsRange, ...filter }: FormState, pagination?: PaginationParams) {
    this.filterOptionsService?.setFilters({
      brands: filter.brandIds || [],
      categories: filter.category || [],
    });
    this.urlFiltersService.setFilters({ isAvailablePointsRange, ...filter });
    return this.productListFacade.getProducts(
      { ...filter, ...this.getPriceFilters(isAvailablePointsRange) },
      pagination,
    );
  }

  private getPriceFilters(isEnabled: boolean): PriceFilters {
    if (!isEnabled) {
      return {};
    }
    return {
      priceFrom: 0,
      // 1 Shopping Dollar = 1 USD, so the balance is the upper price bound.
      priceTo: this.userService.points(),
    };
  }

  private initFilters(): void {
    const urlFilters = this.urlFiltersService.filters();
    const initialFilters: FormState = { ...this.initialFilters(), ...urlFilters };

    if (Object.keys(urlFilters).length) {
      this.initialFilters.set(initialFilters);
    }
  }

  private initFetching(): void {
    const initialFilters = this.initialFilters();
    // if isAvailablePointsRange is true, we need to wait for user data to be loaded, to get the balance
    if (initialFilters.isAvailablePointsRange) {
      this.userSubscription?.unsubscribe();
      this.userSubscription = this.userService.isUserLoaded$
        .pipe(
          filter(Boolean),
          tap(() => this.onFiltersChanged(initialFilters)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    } else {
      this.onFiltersChanged(initialFilters);
    }
  }

  private bindRouteChangeToFilterRefetch(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        tap(() => {
          // Skip refetch if we're restoring from history state to preserve restored products and scroll position
          if (this.isRestoringFromHistory) {
            return;
          }

          this.userSubscription?.unsubscribe();
          this.initialFilters.set(DEFAULT_FILTERS_FORM_STATE);
          this.filterComponent()?.clearForm();
          this.filterOptionsService?.resetSelectedFilters();
          this.initFetching();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  onProductClick(event: ProductDetails): void {
    const currentScrollY = window.scrollY;
    const products = this.productListFacade.products();
    const totalCount = this.productListFacade.totalCount();
    const filters = this.productListFacade.filters();
    const currentRouteUrl = this.router.url;

    // Save state to service (survives component destruction/recreation)
    const stateToSave = {
      products,
      totalCount,
      filters,
      scrollY: currentScrollY,
      routeUrl: currentRouteUrl,
    };

    this.productListStateService.saveState(currentRouteUrl, stateToSave);

    this.onShowProduct.emit(event);
  }
}
